import { RECIPIENT_DRAWER_FLOW_CONFIG } from "./flowConfig";
import { isRequirementMet } from "./utils";

import type { RecipientDrawerFlow, RecipientPaymentMethod } from "./flowConfig";

// "perSection": each section saves itself with its own API call, like the old drawer.
// "single": method forms add to a draft and one footer Save sends it all.
// Both exist so the new drawer can keep the old drawer's save behavior until we fully migrate over
type DrawerSaveModel = "perSection" | "single";

type DrawerSection = "profile" | "paymentMethods";

// a section whose form can hold input that hasn't been added yet
export type DrawerFormSection = "profile" | RecipientPaymentMethod;

// tracks where the drawer is in the save process
type DrawerSaveStep =
  | "form"
  | "upcomingPaymentsWarning"
  | "changeLog"
  | "submitting";

type DrawerLeave =
  | { type: "close" }
  | { type: "navigate"; direction: "previous" | "next" };

interface DrawerFormState {
  flow: RecipientDrawerFlow;
  saveModel: DrawerSaveModel;
  expandedSections: Record<DrawerSection, boolean>;
  openMethod: RecipientPaymentMethod | null;
  // the open form with input not added yet; shows the inline error
  unsavedError: DrawerFormSection | null;
  // a close or move waiting on the "close without saving?" dialog
  pendingAction: DrawerLeave | null;
  step: DrawerSaveStep;
  // set when the drawer should close or move to another recipient now
  leave: DrawerLeave | null;
  // per-section only: a section was saved this session; picks the toast on close
  hasSaved: boolean;
}

interface CreateDrawerState extends DrawerFormState {
  mode: "create";
  profileDone: boolean;
  // per-section only: the recipient Profile's save created
  recipientId: API.RecipientId | null;
}

interface EditDrawerState extends DrawerFormState {
  mode: "edit";
  recipientId: API.RecipientId;
}

interface ViewDrawerState {
  flow: RecipientDrawerFlow;
  saveModel: DrawerSaveModel;
  mode: "view";
  recipientId: API.RecipientId;
  leave: DrawerLeave | null;
}

type FormDrawerState = CreateDrawerState | EditDrawerState;
type DrawerState = FormDrawerState | ViewDrawerState;

// what to pass in to say how the drawer opens
type DrawerStart =
  | { mode: "create" }
  | { mode: "edit"; recipientId: API.RecipientId }
  | { mode: "view"; recipientId: API.RecipientId };

type DrawerAction =
  | { type: "profileCompleted" }
  | {
      type: "sectionToggleRequested";
      section: DrawerSection;
      isOpenFormDirty: boolean;
    }
  | {
      type: "methodOpenRequested";
      method: RecipientPaymentMethod;
      isOpenFormDirty: boolean;
    }
  | { type: "methodCommitted" }
  | { type: "methodCancelled" }
  // per-section only: a section's own save went through
  | { type: "profileSaved"; recipientId: API.RecipientId }
  | { type: "methodSaved" }
  | {
      type: "saveRequested";
      isOpenFormDirty: boolean;
      hasUpcomingPayments: boolean;
    }
  | { type: "upcomingPaymentsAcknowledged" }
  | { type: "changeLogSubmitted" }
  | { type: "saveStepCancelled" }
  | { type: "saveSucceeded"; recipientId: API.RecipientId }
  | { type: "saveFailed"; errorSection: DrawerFormSection | null }
  | {
      type: "leaveRequested";
      leave: DrawerLeave;
      isOpenFormDirty: boolean;
      isDraftDirty: boolean;
    }
  | { type: "cancelRequested" }
  | { type: "discardConfirmed" }
  | { type: "discardCancelled" }
  | { type: "editRequested" };

const FRESH_FORM = {
  unsavedError: null,
  pendingAction: null,
  step: "form",
  leave: null,
  hasSaved: false,
} as const;

// in a payment flow, edit opens on the flow's required method, whether or not the recipient has it.
// Settings has no required method, so it opens on Profile
const getEditSections = (
  flow: RecipientDrawerFlow,
): Pick<DrawerFormState, "expandedSections" | "openMethod"> => {
  const { requiredMethod } = RECIPIENT_DRAWER_FLOW_CONFIG[flow];
  if (requiredMethod) {
    return {
      expandedSections: { profile: false, paymentMethods: true },
      openMethod: requiredMethod,
    };
  }
  return {
    expandedSections: { profile: true, paymentMethods: true },
    openMethod: null,
  };
};

export const initialDrawerState = (
  flow: RecipientDrawerFlow,
  start: DrawerStart,
  saveModel: DrawerSaveModel,
): DrawerState => {
  switch (start.mode) {
    case "create":
      return {
        ...FRESH_FORM,
        flow,
        saveModel,
        mode: "create",
        profileDone: false,
        recipientId: null,
        expandedSections: { profile: true, paymentMethods: false },
        openMethod: null,
      };
    case "edit":
      return {
        ...FRESH_FORM,
        flow,
        saveModel,
        mode: "edit",
        recipientId: start.recipientId,
        ...getEditSections(flow),
      };
    default:
      return {
        flow,
        saveModel,
        mode: "view",
        recipientId: start.recipientId,
        leave: null,
      };
  }
};

// Payment methods stays locked, and Save is disabled, until Profile is continued
const isProfilePending = (state: FormDrawerState) =>
  state.mode === "create" && !state.profileDone;

// returns the open section whose input would be lost if the drawer closed now:
// - the open payment method form
// - Profile, per-section: whenever it's expanded, since its input waits for Profile's own Save
// - Profile, single: only in create before Continue. Otherwise its input is already in the draft
export const getOpenFormSection = (
  state: FormDrawerState,
): DrawerFormSection | null => {
  if (state.openMethod) return state.openMethod;
  const isProfileForm =
    state.saveModel === "perSection" || isProfilePending(state);
  if (isProfileForm && state.expandedSections.profile) return "profile";
  return null;
};

const blockOnUnsavedInput = (
  state: FormDrawerState,
  isOpenFormDirty: boolean,
): FormDrawerState | null => {
  const section = isOpenFormDirty ? getOpenFormSection(state) : null;
  return section ? { ...state, unsavedError: section } : null;
};

const openSectionWithError = (
  state: FormDrawerState,
  errorSection: DrawerFormSection | null,
): FormDrawerState => {
  if (!errorSection) return state;
  if (errorSection === "profile") {
    return {
      ...state,
      expandedSections: { ...state.expandedSections, profile: true },
    };
  }
  return {
    ...state,
    expandedSections: { ...state.expandedSections, paymentMethods: true },
    openMethod: errorSection,
  };
};

const viewReducer = (
  state: ViewDrawerState,
  action: DrawerAction,
): DrawerState => {
  switch (action.type) {
    case "leaveRequested":
      return { ...state, leave: action.leave };
    case "editRequested":
      return initialDrawerState(
        state.flow,
        { mode: "edit", recipientId: state.recipientId },
        state.saveModel,
      );
    default:
      return state;
  }
};

const formReducer = (
  state: FormDrawerState,
  action: DrawerAction,
): DrawerState => {
  const config = RECIPIENT_DRAWER_FLOW_CONFIG[state.flow];

  // nothing but the save result is handled while a save is in flight
  if (
    state.step === "submitting" &&
    action.type !== "saveSucceeded" &&
    action.type !== "saveFailed"
  ) {
    return state;
  }

  switch (action.type) {
    case "profileCompleted":
      if (state.saveModel !== "single") return state;
      if (state.mode !== "create" || state.profileDone) return state;
      return {
        ...state,
        profileDone: true,
        expandedSections: { profile: false, paymentMethods: true },
        openMethod: config.requiredMethod,
        unsavedError: null,
      };

    case "sectionToggleRequested": {
      const blocked = blockOnUnsavedInput(state, action.isOpenFormDirty);
      if (blocked) return blocked;
      if (action.section === "paymentMethods" && isProfilePending(state)) {
        return state;
      }
      const isExpanded = !state.expandedSections[action.section];
      // one form open at a time, so opening or collapsing a section closes the open method form.
      const closesMethod =
        action.section === "paymentMethods"
          ? !isExpanded
          : isExpanded && state.saveModel === "perSection";
      return {
        ...state,
        expandedSections: {
          ...state.expandedSections,
          [action.section]: isExpanded,
        },
        openMethod: closesMethod ? null : state.openMethod,
        unsavedError: null,
      };
    }

    case "methodOpenRequested": {
      if (isProfilePending(state)) return state;
      const openForm = getOpenFormSection(state);
      // with a single save, other methods' Add is disabled while a method is open
      if (openForm && state.saveModel === "single") return state;
      const blocked = blockOnUnsavedInput(state, action.isOpenFormDirty);
      if (blocked) return blocked;
      // per-section: an unchanged open form closes, so one form is open at a time
      return {
        ...state,
        expandedSections: {
          profile:
            openForm === "profile" ? false : state.expandedSections.profile,
          paymentMethods: true,
        },
        openMethod: action.method,
        unsavedError: null,
      };
    }

    case "methodCommitted":
      if (state.saveModel !== "single") return state;
      return { ...state, openMethod: null, unsavedError: null };

    case "methodCancelled":
      return { ...state, openMethod: null, unsavedError: null };

    case "profileSaved":
      if (state.saveModel !== "perSection") return state;
      // the first save in create creates the recipient, then moves on to the required method
      if (state.mode === "create" && !state.profileDone) {
        return {
          ...state,
          recipientId: action.recipientId,
          profileDone: true,
          hasSaved: true,
          expandedSections: { profile: false, paymentMethods: true },
          openMethod: config.requiredMethod,
          unsavedError: null,
        };
      }
      return {
        ...state,
        hasSaved: true,
        expandedSections: { ...state.expandedSections, profile: false },
        unsavedError: null,
      };

    case "methodSaved":
      if (state.saveModel !== "perSection") return state;
      return { ...state, openMethod: null, unsavedError: null, hasSaved: true };

    // per-section: has no footer save
    case "saveRequested": {
      if (state.saveModel !== "single") return state;
      if (state.step !== "form" || isProfilePending(state)) return state;
      const blocked = blockOnUnsavedInput(state, action.isOpenFormDirty);
      if (blocked) return blocked;
      if (state.mode === "create") {
        return { ...state, step: "submitting", unsavedError: null };
      }
      return {
        ...state,
        step: action.hasUpcomingPayments
          ? "upcomingPaymentsWarning"
          : "changeLog",
        unsavedError: null,
      };
    }

    case "upcomingPaymentsAcknowledged":
      if (state.step !== "upcomingPaymentsWarning") return state;
      return { ...state, step: "changeLog" };

    // the change log's save button is the real save in edit
    case "changeLogSubmitted":
      if (state.step !== "changeLog") return state;
      return { ...state, step: "submitting" };

    // the draft is untouched and still counts as unsaved
    case "saveStepCancelled":
      if (state.step === "form") return state;
      return { ...state, step: "form" };

    case "saveSucceeded":
      if (state.step !== "submitting") return state;
      if (config.afterSave === "view") {
        return initialDrawerState(
          state.flow,
          { mode: "view", recipientId: action.recipientId },
          state.saveModel,
        );
      }
      return { ...state, leave: { type: "close" } };

    case "saveFailed":
      if (state.step !== "submitting") return state;
      return openSectionWithError(
        { ...state, step: "form" },
        action.errorSection,
      );

    case "leaveRequested": {
      const blocked = blockOnUnsavedInput(state, action.isOpenFormDirty);
      if (blocked) return blocked;
      // per-section: nothing is left unsaved once the forms are closed
      if (action.isDraftDirty && state.saveModel === "single") {
        return { ...state, pendingAction: action.leave };
      }
      return { ...state, leave: action.leave };
    }

    // footer Cancel discards straight away, without the confirmation dialog
    case "cancelRequested":
      return { ...state, leave: { type: "close" } };

    case "discardConfirmed":
      if (!state.pendingAction) return state;
      return { ...state, pendingAction: null, leave: state.pendingAction };

    case "discardCancelled":
      return { ...state, pendingAction: null };

    default:
      return state;
  }
};

// per-section only: whether Done is enabled. Disabled while the open form has unsaved input.
// every flow: in create, the saved recipient also needs this flow's required method
export const canFinish = (
  state: DrawerState,
  recipient: API.Recipient | undefined,
  isOpenFormDirty: boolean,
) => {
  if (state.mode === "view") return true;
  if (isOpenFormDirty) return false;
  if (state.mode === "edit") return true;
  return recipient ? isRequirementMet(recipient, state.flow) : false;
};

export const drawerReducer = (
  state: DrawerState,
  action: DrawerAction,
): DrawerState =>
  state.mode === "view"
    ? viewReducer(state, action)
    : formReducer(state, action);
